import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import pepAnim from "../../../images/pep-lottie.json";
import sanctionsAnim from "../../../images/sanctions-lottie.json";
import "../mobile/KycMobileClientSections.css";

function InlineDangerMessage({ children }) {
  return (
    <div className="kycInlineFieldError">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

function autoResizeTextarea(el) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * 3 + verticalExtras;
  const maxHeight = lineHeight * 10 + verticalExtras;
  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  el.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

const DETAIL_TEXTAREA_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";
const DECLARATION_OPTIONS = [
  { value: "false", label: "No" },
  { value: "true", label: "Yes" },
];

function declarationValue(value) {
  return typeof value === "boolean" ? String(value) : "";
}

function declarationLabel(value) {
  const currentValue = declarationValue(value);
  return (
    DECLARATION_OPTIONS.find((option) => option.value === currentValue)?.label ||
    "Select"
  );
}

export default function KycSectionPepSanctions({
  sectionNo = 0,
  form,
  onSetAt,
  mode = "staff", // "client" | "staff" | "review"
  changedClass, // optional (pathArray) => "kycChanged"
  readOnly = false,
  copyOverrides = {},
  submitAttempted = false,
}) {
  const pep = form?.pepSanctions || {};
  const isEntity = form?.customer?.type === "entity";
  const useCardLayout = mode === "client" || mode === "review";
  const missingPepDeclaration =
    submitAttempted && typeof pep.pepDeclared !== "boolean";
  const missingSanctionsDeclaration =
    submitAttempted && typeof pep.sanctionsDeclared !== "boolean";

  const pepDetailsRef = useRef(null);
  const sanctionsDetailsRef = useRef(null);
  const declarationBtnRefs = useRef({});
  const [openDeclarationDropdown, setOpenDeclarationDropdown] = useState(null);
  const [declarationDropdownPos, setDeclarationDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useLayoutEffect(() => {
    if (!useCardLayout) return;
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(pepDetailsRef.current);
      autoResizeTextarea(sanctionsDetailsRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    pep.pepDetails,
    pep.sanctionsDetails,
    pep.pepDeclared,
    pep.sanctionsDeclared,
    useCardLayout,
  ]);

  // ✅ safe wrapper for changedClass
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const openDeclarationPicker = (key) => {
    if (readOnly) return;
    if (openDeclarationDropdown === key) {
      setOpenDeclarationDropdown(null);
      return;
    }
    const rect = declarationBtnRefs.current[key]?.getBoundingClientRect?.();
    if (rect) {
      setDeclarationDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpenDeclarationDropdown(key);
  };

  useLayoutEffect(() => {
    if (!openDeclarationDropdown) return undefined;

    const updateDropdownPosition = () => {
      const rect =
        declarationBtnRefs.current[
          openDeclarationDropdown
        ]?.getBoundingClientRect?.();
      if (!rect) {
        setOpenDeclarationDropdown(null);
        return;
      }
      setDeclarationDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [openDeclarationDropdown]);

  const renderDeclarationPicker = ({ dropdownKey, value, path, className }) => {
    const currentValue = declarationValue(value);
    return (
      <>
        <button
          ref={(el) => {
            declarationBtnRefs.current[dropdownKey] = el;
          }}
          type="button"
          className={className}
          disabled={readOnly}
          onClick={() => openDeclarationPicker(dropdownKey)}
        >
          <span className="kycPepDeclarationPickerText">
            {declarationLabel(value)}
          </span>
        </button>
        {!readOnly &&
        openDeclarationDropdown === dropdownKey &&
        typeof document !== "undefined"
          ? createPortal(
              <>
                <div
                  className="kycPepDeclarationDismiss"
                  onClick={() => setOpenDeclarationDropdown(null)}
                />
                <div
                  className="kycPepDeclarationDropdown"
                  style={{
                    top: declarationDropdownPos.top,
                    left: declarationDropdownPos.left,
                    minWidth: declarationDropdownPos.width,
                  }}
                >
                  {DECLARATION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        "kycPepDeclarationOption" +
                        (currentValue === option.value ? " is-active" : "")
                      }
                      onClick={() => {
                        onSetAt(path, option.value === "true");
                        setOpenDeclarationDropdown(null);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )
          : null}
      </>
    );
  };

  // ✅ Copy by mode
  const COPY = {
    client: {
      header: {
        kicker: "Section 4",
        title: " | Political links & Sanctions",
        hint: "Please disclose any political relationships or links to sanction programs",
      },
      fields: {
        pepLabel: isEntity
          ? "Does any beneficial owner, controlling person, authorised signatory, director, or other person connected with the entity hold, or have they ever held, a prominent public function, or are they a close family member or close associate of a person who does or has held such a function?"
          : "Does the individual hold, or have they ever held, a prominent public function, or are they a close family member or close associate of a person who does or has held such a function?",
        pepDetails: "Provide names, position, relationship, and country.",
        sancLabel: isEntity
          ? "Is the entity, or any beneficial owner, controlling person, authorised signatory, director, or other connected person connected with it, subject to any sanctions measures or terrorism financing restrictions?"
          : "Is the individual subject to any sanctions measures or terrorism financing restrictions?",
        sancDetails: "Explain the relevant sanctions exposure.",
      },
    },

    staff: {
      header: {
        kicker: null,
        title: " | PEP & Sanctions",
        hint: null,
      },
      fields: {
        pepLabel: "PEP exposure?",
        pepHelp: null,
        pepDetails: "PEP details",
        sancLabel: "Sanctions exposure?",
        sancHelp: null,
        sancDetails: "Sanctions details",
      },
    },

    review: {
      header: {
        kicker: null,
        title: " | PEP & Sanctions",
        hint: null,
      },
      fields: {
        pepLabel: "Client declaration on political exposure",
        pepHelp: null,
        pepDetails: "PEP details",
        sancLabel: "Client declaration on sanctions restrictions",
        sancHelp: null,
        sancDetails: "Sanctions/TFS details",
      },
    },
  };

  const baseCopy = COPY[mode] || COPY.staff;
  const copy = {
    ...baseCopy,
    ...copyOverrides,
    header: { ...baseCopy.header, ...(copyOverrides.header || {}) },
    fields: { ...baseCopy.fields, ...(copyOverrides.fields || {}) },
  };

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        {copy.header.kicker ? (
          <div className="kycSectionKicker">{copy.header.kicker}</div>
        ) : null}

        <h5 className="kycSectionTitle">
          {sectionNo}
          {copy.header.title}
        </h5>

        {copy.header.hint ? (
          <div className="kycSectionHint">{copy.header.hint}</div>
        ) : null}
      </div>

      <div className="kycSectionBody">
        {useCardLayout ? (
          /* ── Client mode: mobile-card design, side by side ── */
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Question cards — always same height, independent of details below */}
            <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
              {/* PEP card */}
              <div className="kycMobileQuestionCard" style={{ flex: 1 }}>
                <div className="kycMobileQuestionRail">
                  <div className="kycMobileQuestionLottieWrap">
                    <Lottie
                      animationData={pepAnim}
                      loop
                      autoplay
                      className="kycMobileQuestionLottie"
                    />
                  </div>
                </div>
                <div className="kycMobileQuestionContent">
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: "#5D6771" }}>Political exposure</div>
                  <div className="kycMobileFieldLabel kycMobileFieldLabel--question" style={{ fontWeight: 400, fontSize: 14 }}>
                    {copy.fields.pepLabel}
                  </div>
                  <div className="kycMobileSegment">
                    <button
                      type="button"
                      className={`kycMobileSegmentBtn ${
                        pep.pepDeclared === false ? "is-active" : ""
                      } ${
                        mode === "review" &&
                        pep.pepDeclared === false &&
                        cc(["pepSanctions", "pepDeclared"])
                          ? "kycReviewChangedChip"
                          : ""
                      }`}
                      style={pep.pepDeclared === false ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                      onClick={() => onSetAt(["pepSanctions", "pepDeclared"], false)}
                      disabled={readOnly}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className={`kycMobileSegmentBtn ${
                        pep.pepDeclared === true ? "is-active" : ""
                      } ${
                        mode === "review" &&
                        pep.pepDeclared === true &&
                        cc(["pepSanctions", "pepDeclared"])
                          ? "kycReviewChangedChip"
                          : ""
                      }`}
                      style={pep.pepDeclared === true ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                      onClick={() => onSetAt(["pepSanctions", "pepDeclared"], true)}
                      disabled={readOnly}
                    >
                      Yes
                    </button>
                  </div>
                  {missingPepDeclaration ? (
                    <InlineDangerMessage>
                      Select yes or no for this declaration
                    </InlineDangerMessage>
                  ) : null}
                </div>
              </div>

              {/* Sanctions card */}
              <div className="kycMobileQuestionCard" style={{ flex: 1 }}>
                <div className="kycMobileQuestionRail">
                  <div className="kycMobileQuestionLottieWrap">
                    <Lottie
                      animationData={sanctionsAnim}
                      loop
                      autoplay
                      className="kycMobileQuestionLottie"
                    />
                  </div>
                </div>
                <div className="kycMobileQuestionContent">
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: "#5D6771" }}>Sanctions exposure</div>
                  <div className="kycMobileFieldLabel kycMobileFieldLabel--question" style={{ fontWeight: 400, fontSize: 14 }}>
                    {copy.fields.sancLabel}
                  </div>
                  <div className="kycMobileSegment">
                    <button
                      type="button"
                      className={`kycMobileSegmentBtn ${
                        pep.sanctionsDeclared === false ? "is-active" : ""
                      } ${
                        mode === "review" &&
                        pep.sanctionsDeclared === false &&
                        cc(["pepSanctions", "sanctionsDeclared"])
                          ? "kycReviewChangedChip"
                          : ""
                      }`}
                      style={pep.sanctionsDeclared === false ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                      onClick={() => onSetAt(["pepSanctions", "sanctionsDeclared"], false)}
                      disabled={readOnly}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className={`kycMobileSegmentBtn ${
                        pep.sanctionsDeclared === true ? "is-active" : ""
                      } ${
                        mode === "review" &&
                        pep.sanctionsDeclared === true &&
                        cc(["pepSanctions", "sanctionsDeclared"])
                          ? "kycReviewChangedChip"
                          : ""
                      }`}
                      style={pep.sanctionsDeclared === true ? { background: "#B5C4D0", borderColor: "#B5C4D0" } : {}}
                      onClick={() => onSetAt(["pepSanctions", "sanctionsDeclared"], true)}
                      disabled={readOnly}
                    >
                      Yes
                    </button>
                  </div>
                  {missingSanctionsDeclaration ? (
                    <InlineDangerMessage>
                      Select yes or no for this declaration
                    </InlineDangerMessage>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Details row — below the cards, independent of card heights */}
            <AnimatePresence>
              {pep.pepDeclared || pep.sanctionsDeclared ? (
                <motion.div
                  key="pep-details-row"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                    opacity: { duration: 0.22, delay: 0.1 },
                  }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <AnimatePresence>
                        {pep.pepDeclared ? (
                          <motion.div
                            key="pep-detail-field"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                              opacity: { duration: 0.22, delay: 0.1 },
                            }}
                            style={{ overflow: "hidden" }}
                          >
                            <div className="kycMobileFieldCard">
                              <label className="kycMobileFieldLabel">PEP details</label>
                              <textarea
                                ref={pepDetailsRef}
                                className={`form-control kycMobileAutoGrowTextarea ${cc([
                                  "pepSanctions",
                                  "pepDetails",
                                ])}`}
                                rows={3}
                                style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                                value={pep.pepDetails || ""}
                                onChange={(e) => {
                                  autoResizeTextarea(e.currentTarget);
                                  onSetAt(["pepSanctions", "pepDetails"], e.target.value);
                                }}
                                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                disabled={readOnly}
                                placeholder={copy.fields.pepDetails}
                              />
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                    <div style={{ flex: 1 }}>
                      <AnimatePresence>
                        {pep.sanctionsDeclared ? (
                          <motion.div
                            key="sanctions-detail-field"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                              opacity: { duration: 0.22, delay: 0.1 },
                            }}
                            style={{ overflow: "hidden" }}
                          >
                            <div className="kycMobileFieldCard">
                              <label className="kycMobileFieldLabel">Sanctions details</label>
                              <textarea
                                ref={sanctionsDetailsRef}
                                className={`form-control kycMobileAutoGrowTextarea ${cc([
                                  "pepSanctions",
                                  "sanctionsDetails",
                                ])}`}
                                rows={3}
                                style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                                value={pep.sanctionsDetails || ""}
                                onChange={(e) => {
                                  autoResizeTextarea(e.currentTarget);
                                  onSetAt(["pepSanctions", "sanctionsDetails"], e.target.value);
                                }}
                                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                disabled={readOnly}
                                placeholder={copy.fields.sancDetails}
                              />
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : (
          /* ── Staff / review mode: original layout unchanged ── */
          <div className="row g-3">
            <div className="col-6">
              <div className="text-muted small mb-1">{copy.fields.pepLabel}</div>
            </div>
            <div className="col-6">
              <div className="text-muted small mb-1">{copy.fields.sancLabel}</div>
            </div>
            <div className="col-6" style={{ marginTop: 5 }}>
              {renderDeclarationPicker({
                dropdownKey: "pepDeclared",
                value: pep.pepDeclared,
                path: ["pepSanctions", "pepDeclared"],
                className:
                  "form-select kycPepDeclarationPicker " +
                  cc(["pepSanctions", "pepDeclared"]),
              })}
              {missingPepDeclaration ? (
                <InlineDangerMessage>
                  Select yes or no for this declaration
                </InlineDangerMessage>
              ) : null}
              {copy.fields.pepHelp ? (
                <div className="text-muted small mt-1">{copy.fields.pepHelp}</div>
              ) : null}
            </div>
            <div className="col-6" style={{ marginTop: 5 }}>
              {renderDeclarationPicker({
                dropdownKey: "sanctionsDeclared",
                value: pep.sanctionsDeclared,
                path: ["pepSanctions", "sanctionsDeclared"],
                className:
                  "form-select kycPepDeclarationPicker " +
                  cc(["pepSanctions", "sanctionsDeclared"]),
              })}
              {missingSanctionsDeclaration ? (
                <InlineDangerMessage>
                  Select yes or no for this declaration
                </InlineDangerMessage>
              ) : null}
              {copy.fields.sancHelp ? (
                <div className="text-muted small mt-1">
                  {copy.fields.sancHelp}
                </div>
              ) : null}
            </div>
            <div className="col-6" style={{ marginTop: 20 }}>
              <div className="text-muted small mb-1">
                {copy.fields.pepDetails}
              </div>
              <textarea
                className={"form-control " + cc(["pepSanctions", "pepDetails"])}
                rows={5}
                disabled={readOnly}
                value={pep.pepDetails || ""}
                onChange={(e) =>
                  onSetAt(["pepSanctions", "pepDetails"], e.target.value)
                }
              />
            </div>
            <div className="col-6" style={{ marginTop: 20 }}>
              <div className="text-muted small mb-1">
                {copy.fields.sancDetails}
              </div>
              <textarea
                className={
                  "form-control " + cc(["pepSanctions", "sanctionsDetails"])
                }
                rows={5}
                disabled={readOnly}
                value={pep.sanctionsDetails || ""}
                onChange={(e) =>
                  onSetAt(["pepSanctions", "sanctionsDetails"], e.target.value)
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
